import {
  type ColumnFiltersState,
  type FilterFn,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type ExpandedState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  type TableState,
  type Updater,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  type SingleParser,
  type UseQueryStateOptions,
  useQueryState,
  useQueryStates,
} from "nuqs";
import * as React from "react";

import { useDebouncedCallback } from "@/hooks/use-debounced-callback";
import { getSortingStateParser } from "@/lib/parsers";
import type { ExtendedColumnSort, QueryKeys } from "@/types/data-table";

function parseDateFilterToken(value: unknown): Date | undefined {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value) || Math.abs(value) < 1e10) {
      return undefined;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  if (typeof value === "string") {
    const normalized = value.trim();
    if (!normalized) return undefined;

    const isTimestamp = /^\d{11,}$/.test(normalized);
    const isDateString =
      /^\d{4}-\d{1,2}-\d{1,2}/.test(normalized) ||
      /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(normalized) ||
      normalized.includes("T");

    if (!isTimestamp && !isDateString) {
      return undefined;
    }

    const parsed = isTimestamp
      ? new Date(Number(normalized))
      : new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  return undefined;
}

function isSameCalendarDay(left: Date, right: Date) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function getDateFilterMatch(
  cellRawValue: unknown,
  rawFilterValues: unknown[],
): boolean | null {
  const normalizedFilterValues = rawFilterValues.filter(
    (value) =>
      value !== null &&
      value !== undefined &&
      String(value).trim().length > 0,
  );

  if (normalizedFilterValues.length === 0) return null;

  const filterDates = normalizedFilterValues
    .map((value) => parseDateFilterToken(value))
    .filter((value): value is Date => Boolean(value));

  if (filterDates.length === 0) return null;
  if (filterDates.length !== normalizedFilterValues.length) return null;

  const cellDate = parseDateFilterToken(cellRawValue);
  if (!cellDate) return false;

  const [firstDate, secondDate] = filterDates;
  if (!firstDate) return null;

  if (filterDates.length === 1) {
    return isSameCalendarDay(cellDate, firstDate);
  }

  const from = new Date(firstDate);
  from.setHours(0, 0, 0, 0);

  const to = new Date(secondDate ?? firstDate);
  to.setHours(23, 59, 59, 999);

  const cellTime = cellDate.getTime();
  return cellTime >= from.getTime() && cellTime <= to.getTime();
}

function getNumericRangeMatch(
  cellRawValue: unknown,
  filterValue: unknown[],
): boolean | null {
  if (filterValue.length !== 2) return null;

  const [minVal, maxVal] = filterValue;
  if (typeof minVal !== "number" || typeof maxVal !== "number") return null;

  const cellNum =
    typeof cellRawValue === "number"
      ? cellRawValue
      : typeof cellRawValue === "string"
        ? Number(cellRawValue)
        : undefined;

  if (cellNum === undefined || Number.isNaN(cellNum)) return false;

  return cellNum >= minVal && cellNum <= maxVal;
}

const multiValueFilter: FilterFn<unknown> = (row, columnId, filterValue) => {
  const cellRawValue = row.getValue(columnId);

  if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0))
    return true;

  const cellValue = String(row.getValue(columnId) ?? "").toLowerCase();
  if (Array.isArray(filterValue)) {
    const rangeMatch = getNumericRangeMatch(cellRawValue, filterValue);
    if (rangeMatch !== null) return rangeMatch;

    const dateMatch = getDateFilterMatch(cellRawValue, filterValue);
    if (dateMatch !== null) return dateMatch;

    return filterValue.some(
      (v: string) => cellValue === String(v).toLowerCase(),
    );
  }

  const dateMatch = getDateFilterMatch(cellRawValue, [filterValue]);
  if (dateMatch !== null) return dateMatch;

  return cellValue.includes(String(filterValue).toLowerCase());
};

const PAGE_KEY = "page";
const PER_PAGE_KEY = "perPage";
const SORT_KEY = "sort";
const FILTERS_KEY = "filters";
const JOIN_OPERATOR_KEY = "joinOperator";
const ARRAY_SEPARATOR = ",";
const DEBOUNCE_MS = 300;
const THROTTLE_MS = 50;

interface UseDataTableProps<TData>
  extends Omit<
      TableOptions<TData>,
      | "state"
      | "getCoreRowModel"
    > {
  pageCount?: number;
  initialState?: Omit<Partial<TableState>, "sorting"> & {
    sorting?: ExtendedColumnSort<TData>[];
  };
  queryKeys?: Partial<QueryKeys>;
  history?: "push" | "replace";
  debounceMs?: number;
  throttleMs?: number;
  clearOnDefault?: boolean;
  enableAdvancedFilter?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  startTransition?: UseQueryStateOptions<string>["startTransition"];
}

export function useDataTable<TData>(props: UseDataTableProps<TData>) {
  const {
    columns,
    pageCount,
    manualPagination = false,
    manualSorting = false,
    manualFiltering = false,
    initialState,
    queryKeys,
    history = "replace",
    debounceMs = DEBOUNCE_MS,
    throttleMs = THROTTLE_MS,
    clearOnDefault = false,
    enableAdvancedFilter = false,
    scroll = false,
    shallow = true,
    startTransition,
    ...tableProps
  } = props;
  const pageKey = queryKeys?.page ?? PAGE_KEY;
  const perPageKey = queryKeys?.perPage ?? PER_PAGE_KEY;
  const sortKey = queryKeys?.sort ?? SORT_KEY;
  const filtersKey = queryKeys?.filters ?? FILTERS_KEY;
  const joinOperatorKey = queryKeys?.joinOperator ?? JOIN_OPERATOR_KEY;

  const queryStateOptions = React.useMemo<
    Omit<UseQueryStateOptions<string>, "parse">
  >(
    () => ({
      history,
      scroll,
      shallow,
      throttleMs,
      debounceMs,
      clearOnDefault,
      startTransition,
    }),
    [
      history,
      scroll,
      shallow,
      throttleMs,
      debounceMs,
      clearOnDefault,
      startTransition,
    ],
  );

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>(
    initialState?.rowSelection ?? {},
  );
  const [expanded, setExpanded] = React.useState<ExpandedState>(
    initialState?.expanded ?? {},
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>(initialState?.columnVisibility ?? {});

  const [page, setPage] = useQueryState(
    pageKey,
    parseAsInteger.withOptions(queryStateOptions).withDefault(1),
  );
  const [perPage, setPerPage] = useQueryState(
    perPageKey,
    parseAsInteger
      .withOptions(queryStateOptions)
      .withDefault(initialState?.pagination?.pageSize ?? 10),
  );

  const pagination: PaginationState = React.useMemo(() => {
    return {
      pageIndex: page - 1, // zero-based index -> one-based index
      pageSize: perPage,
    };
  }, [page, perPage]);

  const onPaginationChange = React.useCallback(
    (updaterOrValue: Updater<PaginationState>) => {
      if (typeof updaterOrValue === "function") {
        const newPagination = updaterOrValue(pagination);
        void setPage(newPagination.pageIndex + 1);
        void setPerPage(newPagination.pageSize);
      } else {
        void setPage(updaterOrValue.pageIndex + 1);
        void setPerPage(updaterOrValue.pageSize);
      }
    },
    [pagination, setPage, setPerPage],
  );

  const columnIds = React.useMemo(() => {
    return new Set(
      columns.map((column) => column.id).filter(Boolean) as string[],
    );
  }, [columns]);

  const [sorting, setSorting] = useQueryState(
    sortKey,
    getSortingStateParser<TData>(columnIds)
      .withOptions(queryStateOptions)
      .withDefault(initialState?.sorting ?? []),
  );

  const onSortingChange = React.useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      if (typeof updaterOrValue === "function") {
        const newSorting = updaterOrValue(sorting);
        setSorting(newSorting as ExtendedColumnSort<TData>[]);
      } else {
        setSorting(updaterOrValue as ExtendedColumnSort<TData>[]);
      }
    },
    [sorting, setSorting],
  );

  const filterableColumns = React.useMemo(() => {
    if (enableAdvancedFilter) return [];

    return columns.filter((column) => column.enableColumnFilter);
  }, [columns, enableAdvancedFilter]);

  const filterParsers = React.useMemo(() => {
    if (enableAdvancedFilter) return {};

    return filterableColumns.reduce<
      Record<string, SingleParser<string> | SingleParser<string[]>>
    >((acc, column) => {
      if (column.meta?.options) {
        acc[column.id ?? ""] = parseAsArrayOf(
          parseAsString,
          ARRAY_SEPARATOR,
        ).withOptions(queryStateOptions);
      } else {
        acc[column.id ?? ""] = parseAsString.withOptions(queryStateOptions);
      }
      return acc;
    }, {});
  }, [filterableColumns, queryStateOptions, enableAdvancedFilter]);

  const [filterValues, setFilterValues] = useQueryStates(filterParsers);

  const debouncedSetFilterValues = useDebouncedCallback(
    (values: typeof filterValues) => {
      void setPage(1);
      void setFilterValues(values);
    },
    debounceMs,
  );

  const initialColumnFilters: ColumnFiltersState = React.useMemo(() => {
    if (enableAdvancedFilter) {
      return (initialState?.columnFilters as ColumnFiltersState | undefined) ?? [];
    }

    const urlColumnFilters = Object.entries(filterValues).reduce<ColumnFiltersState>(
      (filters, [key, value]) => {
        if (value !== null) {
          const processedValue = Array.isArray(value) ? value : [value];

          filters.push({
            id: key,
            value: processedValue,
          });
        }
        return filters;
      },
      [],
    );

    if (urlColumnFilters.length > 0) {
      return urlColumnFilters;
    }

    return (initialState?.columnFilters as ColumnFiltersState | undefined) ?? [];
  }, [filterValues, enableAdvancedFilter, initialState?.columnFilters]);

  const [columnFilters, setColumnFilters] =
    React.useState<ColumnFiltersState>(initialColumnFilters);

  const onColumnFiltersChange = React.useCallback(
    (updaterOrValue: Updater<ColumnFiltersState>) => {
      if (enableAdvancedFilter) return;

      setColumnFilters((prev) => {
        const next =
          typeof updaterOrValue === "function"
            ? updaterOrValue(prev)
            : updaterOrValue;

        const filterUpdates = next.reduce<
          Record<string, string | string[] | null>
        >((acc, filter) => {
          if (filterableColumns.find((column) => column.id === filter.id)) {
            acc[filter.id] = filter.value as string | string[];
          }
          return acc;
        }, {});

        for (const prevFilter of prev) {
          if (!next.some((filter) => filter.id === prevFilter.id)) {
            filterUpdates[prevFilter.id] = null;
          }
        }

        debouncedSetFilterValues(filterUpdates);
        return next;
      });
    },
    [debouncedSetFilterValues, filterableColumns, enableAdvancedFilter],
  );

  const table = useReactTable({
    ...tableProps,
    columns,
    initialState,
    pageCount: manualPagination ? pageCount ?? -1 : undefined,
    state: {
      pagination,
      sorting,
      columnVisibility,
      rowSelection,
      expanded,
      columnFilters,
    },
    defaultColumn: {
      ...tableProps.defaultColumn,
      enableColumnFilter: false,
      filterFn: multiValueFilter as FilterFn<TData>,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    onPaginationChange,
    onSortingChange,
    onColumnFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    manualPagination,
    manualSorting,
    manualFiltering,
    // Pagination is synced to the URL (nuqs). Convex realtime updates replace the
    // `data` array often; TanStack's default would reset pageIndex on every data
    // change, so users could never stay on page 2+. Filters still reset page via
    // debouncedSetFilterValues → setPage(1).
    autoResetPageIndex: false,
    meta: {
      ...tableProps.meta,
      queryKeys: {
        page: pageKey,
        perPage: perPageKey,
        sort: sortKey,
        filters: filtersKey,
        joinOperator: joinOperatorKey,
      },
    },
  });

  return React.useMemo(
    () => ({ table, shallow, debounceMs, throttleMs }),
    [table, shallow, debounceMs, throttleMs],
  );
}
